import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalendarDays, MapPin, RotateCcw } from 'lucide-react';

const formatDateRange = (startISO, endISO, timeZone) => {
  if (!startISO) return 'Date will be shared via email.';
  try {
    const start = new Date(startISO);
    const end = endISO ? new Date(endISO) : null;
    const formatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone || undefined
    });

    if (end && formatter.formatRange) {
      return formatter.formatRange(start, end);
    }

    const datePart = formatter.format(start);

    if (!end) return datePart;

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone || undefined
    });

    return `${datePart} – ${timeFormatter.format(end)}`;
  } catch (error) {
    console.warn('Unable to format booking time:', error);
    return new Date(startISO).toLocaleString();
  }
};

const computeRemaining = (expiresAtISO) => {
  if (!expiresAtISO) return 0;
  const target = new Date(expiresAtISO).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(target - Date.now(), 0);
};

export default function BookingConfirmationCard({
  summary,
  onRestartBooking
}) {
  const [remainingMs, setRemainingMs] = useState(() =>
    computeRemaining(summary?.holdExpiresAt)
  );
  const hasTriggeredRestartRef = useRef(false);

  useEffect(() => {
    setRemainingMs(computeRemaining(summary?.holdExpiresAt));
    hasTriggeredRestartRef.current = false;
    const interval = setInterval(() => {
      setRemainingMs(computeRemaining(summary?.holdExpiresAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [summary?.holdExpiresAt]);

  const appointmentDate = useMemo(
    () =>
      formatDateRange(summary?.startTime, summary?.endTime, summary?.timezone),
    [summary?.startTime, summary?.endTime, summary?.timezone]
  );

  const remainingDisplay = useMemo(() => {
    const remainingSeconds = Math.floor(remainingMs / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0'
    )}`;
  }, [remainingMs]);

  const handleOpen = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const expired = remainingMs <= 0;

  useEffect(() => {
    if (!expired || hasTriggeredRestartRef.current) return;
    hasTriggeredRestartRef.current = true;
    const timeout = setTimeout(() => {
      onRestartBooking?.();
    }, 5000);
    return () => clearTimeout(timeout);
  }, [expired, onRestartBooking]);

  return (
    <div className="booking-confirmation">
      <div className="booking-confirmation__success-icon" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m9 12 2 2 4-4" />
        </svg>
      </div>
      <div className="booking-confirmation__header">
        <h2 className="section-heading booking-confirmation__heading">
          This meeting is scheduled
        </h2>
        <p className="booking-confirmation__subtext">
          We sent an email with the calendar invitation and details for your record.
        </p>
      </div>

      <div className="booking-confirmation__summary-wrapper">
        <div className="booking-confirmation__summary">
          <div className="booking-confirmation__row">
            <CalendarDays className="booking-confirmation__row-icon" aria-hidden="true" />
            <div>
              <p className="booking-confirmation__label">Date &amp; Time</p>
              <p className="booking-confirmation__value">{appointmentDate}</p>
            </div>
          </div>
          <div className="booking-confirmation__row">
            <MapPin className="booking-confirmation__row-icon" aria-hidden="true" />
            <div>
              <p className="booking-confirmation__label">Location</p>
              <p className="booking-confirmation__value">
                {summary?.address || 'Address confirmed via your calendar email.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className={
          'booking-confirmation__timer' + (expired ? ' booking-confirmation__timer--expired' : '')
        }
      >
        {expired ? (
          <span>Hold expired.</span>
        ) : (
          <>
            <span>Your reservation is held for:</span>
            <strong>{remainingDisplay}</strong>
          </>
        )}
      </div>
      {!expired && (
        <div className="booking-confirmation__restart">
          <button
            type="button"
            className="booking-confirmation__timer-button booking-confirmation__timer-button--soft"
            onClick={() => handleOpen(summary?.rescheduleUrl)}
            disabled={!summary?.rescheduleUrl}
          >
            <RotateCcw className="booking-confirmation__row-icon" aria-hidden="true" />
            Reschedule
          </button>
        </div>
      )}
    </div>
  );
}
